import type { ProfanityLevel } from "../state";
import {
  CENSOR_ALLOW,
  CENSOR_ROOTS,
  CURSES,
  ENDINGS,
  HEADLINES,
  NOTIFICATIONS,
  NOUNS,
  PUNCHLINES,
  PUNCHLINE_NOUNS,
  SIMILES,
  SUBLINES,
  SWEAR_ROOTS,
  type Phase,
  type Tiered,
  type Weighted,
} from "./bank";
import { PACKS } from "./packs";

// ---------- deterministic per-day RNG ----------
// Same event + same calendar day always produces the same picks, so the
// copy holds steady for a whole day (in-app) and a notification fired
// later that day can independently regenerate the same "today" flavour
// without needing to persist what was shown.

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeRng(parts: string[]): () => number {
  return mulberry32(hashStr(parts.join("|")));
}

function pick<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

function pickWeighted(rng: () => number, arr: Weighted[]): string {
  const total = arr.reduce((sum, w) => sum + (typeof w === "string" ? 1 : w[1]), 0);
  let r = rng() * total;
  for (const w of arr) {
    r -= typeof w === "string" ? 1 : w[1];
    if (r < 0) return typeof w === "string" ? w : w[0];
  }
  const last = arr[arr.length - 1];
  return typeof last === "string" ? last : last[0];
}

export function localDayKey(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function daysSinceEpoch(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000);
}

/** Fisher-Yates using the seeded rng, so the shuffle itself is deterministic. */
function shuffledIndices(rng: () => number, n: number): number[] {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Today's item from `pool`, cycling through a fresh shuffle of the whole
 * list before anything repeats, and never landing on the same item two
 * days running across a cycle boundary.
 */
function cyclePick<T>(pool: T[], seed: string[], now: Date): T {
  const n = pool.length;
  if (n === 1) return pool[0];
  const dayNum = daysSinceEpoch(now);
  const cycle = Math.floor(dayNum / n);
  const pos = dayNum % n;
  const permFor = (c: number) => shuffledIndices(makeRng([...seed, String(n), String(c)]), n);
  const perm = permFor(cycle);
  const prevLast = permFor(cycle - 1)[n - 1];
  if (perm[0] === prevLast) [perm[0], perm[1]] = [perm[1], perm[0]];
  return pool[perm[pos]];
}

// ---------- the merged bank ----------

type Slot = "curse" | "pnoun" | "noun" | "ending" | "simile";
const LEVELS: ProfanityLevel[] = [0, 1, 2];

function merge(core: Tiered<string>, key: "curses" | "pnouns" | "nouns" | "endings" | "similes"): Tiered<string> {
  const out = {} as Tiered<string>;
  for (const level of LEVELS) {
    const extra = PACKS.flatMap((p) => p[key]?.[level] ?? []);
    out[level] = [...new Set([...core[level], ...extra])];
  }
  return out;
}

/** Core bank plus every pack, de-duplicated. Exported for the copy lab script. */
export const BANK: Record<Slot, Tiered<string>> = {
  curse: merge(CURSES, "curses"),
  pnoun: merge(PUNCHLINE_NOUNS, "pnouns"),
  noun: merge(NOUNS, "nouns"),
  ending: merge(ENDINGS, "endings"),
  simile: merge(SIMILES, "similes"),
};

// ---------- template filling ----------

export interface CopyContext {
  level: ProfanityLevel;
  eventId: string;
  eventName?: string;
  personName?: string;
  /** ISO date or Date the countdown ends. Drives the phase and {days}. */
  target: string | Date;
  now?: Date;
}

export function daysLeftUntil(target: string | Date, now: Date): number {
  return Math.floor(Math.max(0, new Date(target).getTime() - now.getTime()) / 86_400_000);
}

export function getPhase(daysLeft: number): Phase {
  if (daysLeft <= 0) return "today";
  if (daysLeft === 1) return "tomorrow";
  if (daysLeft <= 7) return "close";
  if (daysLeft <= 60) return "mid";
  return "far";
}

/** "an" before a vowel sound, "a" otherwise. Covers the usual English traps. */
export function withArticle(phrase: string): string {
  const an = /^(?:[aeio]|u(?!ni|s[eu]|r[aeiou]|b[eio])|hour|honest|heir|8|11\b|18\b)/i.test(phrase);
  return `${an ? "an" : "a"} ${phrase}`;
}

interface FillState {
  level: ProfanityLevel;
  rng: () => number;
  vars: { event: string; person: string; days: number };
  used: Map<Slot, Set<string>>;
  /** Pre-chosen words, used the first time their slot appears. */
  preset: Partial<Record<Slot, string>>;
}

const SLOT_RE = /\{(a:|,)?(\w+)\}/g;

/**
 * Fills one template. Returns the text and a copy with the user's own
 * words (event/person names) left out, which is what gets checked for
 * doubled-up swear roots.
 */
function fill(template: string, st: FillState): { text: string; check: string } {
  let check = "";
  const text = template.replace(SLOT_RE, (whole, mod: string | undefined, name: string) => {
    let value: string;
    switch (name) {
      case "event":
        value = st.vars.event || "the big day";
        return value;
      case "person":
        if (mod === ",") return st.vars.person ? `, ${st.vars.person}` : "";
        return st.vars.person || "you";
      case "days":
        return String(st.vars.days);
      case "s":
        return st.vars.days === 1 ? "" : "s";
    }
    if (!(name in BANK)) return whole;
    const slot = name as Slot;
    const used = st.used.get(slot) ?? new Set<string>();
    st.used.set(slot, used);
    const preset = st.preset[slot];
    if (preset && !used.has(preset)) {
      value = preset;
    } else {
      const pool = BANK[slot][st.level].filter((w) => !used.has(w));
      value = pick(st.rng, pool.length ? pool : BANK[slot][st.level]);
    }
    used.add(value);
    check += ` ${value}`;
    return mod === "a:" ? withArticle(value) : value;
  });
  return { text, check: `${check} ${template.replace(SLOT_RE, "")}` };
}

/** Upper-cases the first letter of each sentence after the first (and the first too, if asked). */
function sentenceCase(text: string, capFirst: boolean): string {
  const out = text.replace(/([.!?]\s+)([a-z])/g, (_, gap: string, c: string) => gap + c.toUpperCase());
  return capFirst ? out.charAt(0).toUpperCase() + out.slice(1) : out;
}

// Several lists share the same swear roots ("cunting" in CURSES and "cunt
// of a wait" in NOUNS); picked independently, they can both land in the same
// line. This catches that and re-rolls rather than hand-partitioning every
// list's vocabulary.

/** Counts how many times each root occurs in text (not just whether it's present). */
function rootCounts(text: string): Map<string, number> {
  const lower = text.toLowerCase();
  const counts = new Map<string, number>();
  for (const root of SWEAR_ROOTS) {
    const occurrences = lower.split(root).length - 1;
    if (occurrences > 0) counts.set(root, occurrences);
  }
  return counts;
}

/** True if any swear root turns up more than once across all the parts combined. */
export function hasRootCollision(parts: string[]): boolean {
  const total = new Map<string, number>();
  for (const part of parts) {
    for (const [root, count] of rootCounts(part)) {
      total.set(root, (total.get(root) ?? 0) + count);
    }
  }
  for (const count of total.values()) {
    if (count > 1) return true;
  }
  return false;
}

/**
 * Lab only (the artifact build): step the copy through other days' picks and
 * force a phase, to read every variation without waiting for tomorrow. The
 * day count shown on screen stays real; only the phrase choices move.
 */
export interface CopyPreview {
  dayOffset: number;
  phase: Phase | null;
}
const preview: CopyPreview = { dayOffset: 0, phase: null };
export function setCopyPreview(next: Partial<CopyPreview>) {
  Object.assign(preview, next);
}

function resolve(ctx: CopyContext) {
  const real = ctx.now ?? new Date();
  const days = daysLeftUntil(ctx.target, real);
  const now = preview.dayOffset ? new Date(real.getTime() + preview.dayOffset * 86_400_000) : real;
  return {
    now,
    days,
    phase: preview.phase ?? getPhase(days),
    vars: { event: ctx.eventName?.trim() ?? "", person: ctx.personName?.trim() ?? "", days },
  };
}

// ---------- the punchline card ----------

export interface DailyCopy {
  /** Follows "of " on screen, reading on from "729 hours": "sodding, bloody, gormless bollocks to wade through." */
  main: string;
  /** The smaller second line, which shifts tone as the day gets closer. */
  sub: string;
}

/**
 * Today's punchline and sub-line for this event and tier. Stable all day,
 * new tomorrow. The simile is cycled so it never repeats until the whole
 * list has had a turn; everything else is a fresh roll each day.
 */
export function getDailyCopy(ctx: CopyContext): DailyCopy {
  const { now, phase, vars } = resolve(ctx);
  const level = ctx.level;
  const rng = makeRng([ctx.eventId, localDayKey(now), "web", String(level)]);
  const simile = cyclePick(BANK.simile[level], [ctx.eventId, "simile", String(level)], now);
  const mainTpl = pickWeighted(rng, PUNCHLINES[level]);
  const subTpl = pickWeighted(rng, SUBLINES[phase]);

  let main = "";
  let sub = "";
  for (let attempt = 0; attempt < 40; attempt++) {
    const st: FillState = { level, rng, vars, used: new Map(), preset: { simile } };
    const m = fill(mainTpl, st);
    const s = fill(subTpl, st);
    main = sentenceCase(m.text, false);
    sub = sentenceCase(s.text, true);
    if (!hasRootCollision([m.check, s.check])) break;
  }
  return { main, sub };
}

// ---------- the headline ----------

/**
 * Today's greeting: upbeat, never vulgar. Stable all day, a different one
 * tomorrow, cycled so none repeats until the list is used up. Switches to
 * the phase's own list in the final week.
 */
export function getDailyHeadline(ctx: Omit<CopyContext, "level">): string {
  const { now, phase, vars } = resolve({ ...ctx, level: 0 });
  const pool = phase === "far" ? [...HEADLINES.far, ...HEADLINES.mid] : HEADLINES[phase];
  const template = cyclePick(pool, [ctx.eventId, "headline", phase], now);
  const st: FillState = { level: 0, rng: () => 0, vars, used: new Map(), preset: {} };
  return fill(template, st).text;
}

// ---------- daily notification ----------

/** Today's push-notification body: a different pick from the on-screen copy. */
export function getDailyNotification(ctx: CopyContext, daysLeft: number): string {
  const { now, vars } = resolve(ctx);
  const rng = makeRng([ctx.eventId, localDayKey(now), "notif", String(ctx.level)]);
  let text = "";
  for (let attempt = 0; attempt < 40; attempt++) {
    const template = pickWeighted(rng, NOTIFICATIONS[ctx.level]);
    const st: FillState = { level: ctx.level, rng, vars: { ...vars, days: daysLeft }, used: new Map(), preset: {} };
    const out = fill(template, st);
    text = sentenceCase(out.text, true);
    // Some templates carry their own baked-in swearing ("ya cunt"), so the
    // check covers the template's own words as well as the picked ones.
    if (!hasRootCollision([out.check])) break;
  }
  return text;
}

// ---------- censor ----------

// Any word that starts with a censor root (after an optional "mother"), so
// suffixed forms like "fuckery" or "shitshow" are caught without listing
// every one. Hyphenated compounds are masked piece by piece.
const CENSOR_PATTERN = new RegExp(`\\b(?:bell-?end[a-z]*|(?:mother)?(?:${CENSOR_ROOTS.join("|")})[a-z]*)`, "gi");
const ALLOW = new Set(CENSOR_ALLOW);

function maskWord(word: string): string {
  if (ALLOW.has(word.toLowerCase())) return word;
  if (word.length <= 2) return "*".repeat(word.length);
  return word[0] + "*".repeat(word.length - 2) + word[word.length - 1];
}

/** Masks the hardest swear words. Polite has none to mask, so only Spicy/F**ked need it. */
export function censorText(text: string, level: ProfanityLevel, enabled: boolean): string {
  if (!enabled || level < 1) return text;
  return text.replace(CENSOR_PATTERN, maskWord);
}
